import React, {
  MouseEventHandler,
  Ref,
  UIEventHandler,
  useEffect,
  useRef,
  useState,
  WheelEventHandler,
} from 'react'
import { isDesktop, isMobile } from 'react-device-detect'

import { useContextMotifDetails } from '../../contexts/motifDetailsContext'
import { useContextSelectedMotifs } from '../../contexts/selectedMotifContext'
import { useContextOnboarding } from '../../contexts/onboardingContext'
import { Motif } from '../../types/motifs'
import MotifDetailsItem from './MotifDetailsItem'
import CloseButton from '../CloseButton'
import { cn } from '../../utils/cn'

// const TOP = 64

const MotifDetails = () => {
  const { sortedMotifs, setDetailsOpen, setMotifDetailsFadeOut, areDetailsOpen } =
    useContextMotifDetails()
  const { numberOfSelectedMotifs } = useContextSelectedMotifs()
  const { triggerFirstNotification } = useContextOnboarding()
  const [content, setContent] = useState<Motif[]>([])
  const [scrollPos, setScrollPos] = useState<number>(0)
  const [lastContentIndex, setLastContentIndex] = useState<number>(2)
  const containerRef = useRef<HTMLDivElement>(null)
  const [wrapperClass, setWrapperClass] = useState<string>('fade-in')
  const firstCardRef = useRef<HTMLDivElement>(null)
  const scrollPerItem = useRef<number>(0)

  useEffect(() => {
    if (!sortedMotifs.length) return
    setContent(sortedMotifs.slice(0, 3).filter(Boolean))
    setLastContentIndex(Math.min(2, sortedMotifs.length - 1))
    setScrollPos(0)
  }, [sortedMotifs])

  useEffect(() => {
    if (!firstCardRef.current) return

    scrollPerItem.current =
      isMobile && window.innerHeight < 600
        ? firstCardRef.current?.getBoundingClientRect().height
        : firstCardRef.current?.getBoundingClientRect().width
  }, [firstCardRef.current])

  const handleHorizontalWheelScroll: WheelEventHandler<HTMLDivElement> = event => {
    if (!containerRef?.current) return
    event.stopPropagation()
    containerRef.current.scrollLeft += event.deltaY
  }

  const handleElementScroll: UIEventHandler = event => {
    if (!containerRef?.current) return
    const { scrollLeft, scrollTop } = containerRef.current
    const nextIndex = lastContentIndex + 1
    const scrollOffset = isMobile && window.innerHeight < 600 ? scrollTop : scrollLeft

    if (scrollOffset - scrollPos >= scrollPerItem.current && sortedMotifs[nextIndex]) {
      setScrollPos(scrollOffset)
      setContent([...content, sortedMotifs[nextIndex]])
      setLastContentIndex(nextIndex)
    }
  }

  useEffect(() => {
    if (!containerRef.current) return
    containerRef.current.scrollLeft = 0
  }, [areDetailsOpen])

  const handleClickOutside: MouseEventHandler<HTMLDivElement> = e => {
    if (e.target !== containerRef.current) return

    handleClick()
  }

  const handleClick = () => {
    setWrapperClass('fade-out')
    setMotifDetailsFadeOut(true)
    setTimeout(() => {
      setDetailsOpen(false)
      setMotifDetailsFadeOut(false)
      triggerFirstNotification(numberOfSelectedMotifs)
    }, 500)
  }

  // TODO: uncomment when handleNext bug is being fixed
  // const handleNext = (event: React.MouseEvent<HTMLDivElement>) => {
  //   const left = (event.target as HTMLDivElement).offsetLeft
  //   const offset = firstCardRef.current?.offsetLeft || 0
  //   containerRef.current?.scrollTo({ top: TOP, left: left - offset, behavior: 'smooth' })
  // }

  return (
    <>
      <div
        ref={containerRef as Ref<HTMLDivElement>}
        onScroll={handleElementScroll}
        onWheel={isDesktop ? handleHorizontalWheelScroll : undefined}
        onClick={handleClickOutside}
        className={cn(
          'fixed top-0 right-0 max-h-screen overflow-x-hidden overflow-y-auto flex flex-col items-center gap-[46px] opacity-0 [transform:translateX(50px)] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&.fade-in]:animate-[fade-in-x50_0.5s_ease_forwards] [&.fade-out]:animate-[fade-out-x50_0.5s_ease_forwards] max-sm:short-sm:py-[30px] max-sm:short-sm:px-[90px] sm:flex-row sm:left-0 sm:w-screen sm:min-h-fill-available sm:h-full sm:pl-[172px] sm:overflow-x-auto sm:overflow-y-hidden sm:gap-[72px] xxl:pl-[10vw]',
          wrapperClass,
        )}
      >
        {content.map((motif, index) => (
          <div
            key={index}
            // onClick={isDesktop ? handleNext : undefined}
            id={`detail-item-${index}`}
            className="detail-card"
            ref={index === 0 ? firstCardRef : null}
          >
            <MotifDetailsItem motif={motif} />
          </div>
        ))}
      </div>
      <CloseButton onClick={handleClick} className={cn('CloseButton', wrapperClass)} />
    </>
  )
}

export default MotifDetails
